<script setup lang="ts">
import Review from "@/components/basic/Reviews/Review.vue";

const track = ref<HTMLElement | null>(null);

const reviews = ref([
    {
        title: 'Great and easy-to-use plugin',
        text: 'I was looking for a powerful and easy to use plugin to make nice 3d product presentation for a customer website. This is the best you could ask for: easy to use, feature-rich, and great results. Support was top-tier and very fast.',
        stars: 5,
        link: 'https://wordpress.org/support/topic/great-and-easy-to-use-plugin-57/',
        author: 'mastrognappo',
        source: 'wordpress.org'
    },
    {
        title: 'Exceptional Support for a Fantastic Product',
        text: 'I encountered a bug and the support team was incredibly helpful and responsive. They identified the issue quickly and provided a working solution. The process was smooth and stress-free.',
        stars: 5,
        link: 'https://wordpress.org/support/topic/exceptional-support-for-a-fantastic-product/',
        author: 'bogdanandrei16',
        source: 'wordpress.org'
    },
    {
        title: 'Fantastic Support',
        text: 'I had a few issues related to a non-standard WordPress template, not the plugin itself. Jeroen responded within minutes and spent hours helping with a solution. The plugin is fantastic, and support is even better.',
        stars: 5,
        link: 'https://wordpress.org/support/topic/fantastic-support-534/',
        author: 'sterndale',
        source: 'wordpress.org'
    },
    {
        title: 'Perfect',
        text: 'Works super good, very happy with it. Simple, fast and reliable.',
        stars: 5,
        link: 'https://wordpress.org/support/topic/perfect-9880/',
        author: 'larspepe',
        source: 'wordpress.org'
    },
    {
        title: 'Works',
        text: 'Works great with 360 frames rendered in 3D. Thank you!',
        stars: 5,
        link: 'https://wordpress.org/support/topic/works-2030/',
        author: 'impulsivia',
        source: 'wordpress.org'
    },
    {
        title: 'Nice plugin',
        text: 'Ideal for showing 360° of products.',
        stars: 5,
        link: 'https://wordpress.org/support/topic/nice-plugin-4634/',
        author: 'johntunnel',
        source: 'wordpress.org'
    },
    {
        title: 'Excellent plugin, easy to use',
        text: 'The plugin works great with speed and zoom options. Most importantly, support is quick and effective.',
        stars: 5,
        link: 'https://wordpress.org/support/topic/excellent-plugin-easy-to-use-26/',
        author: 'dasipic',
        source: 'wordpress.org'
    },
    {
        title: '5 Stars Plugin!',
        text: 'Just make the pictures and the plugin will do the rest. The author is very helpful as well.',
        stars: 5,
        link: 'https://wordpress.org/support/topic/5-stars-plugin-14/',
        author: 'socialmadness99',
        source: 'wordpress.org'
    },
    {
        title: 'Finally a professional product presentation!',
        text: 'I can now present product photos in a professional way. Rotation is smooth and natural.',
        stars: 5,
        link: 'https://wordpress.org/support/topic/finally-a-professional-product-presentation/',
        author: 'nkrdbl',
        source: 'wordpress.org'
    },
    {
        title: 'Great plugin',
        text: 'Easy configuration.',
        stars: 5,
        link: 'https://wordpress.org/support/topic/great-plugin-29278/',
        author: 'sannedikkers',
        source: 'wordpress.org'
    },
    {
        title: 'Works great!',
        text: 'Thank you for the service!',
        stars: 5,
        link: 'https://wordpress.org/support/topic/works-great-7393/',
        author: 'miami2001',
        source: 'wordpress.org'
    },
]);

const scrollReviews = (direction: number) => {
    const element = track.value;
    const card = element?.querySelector<HTMLElement>('.review-slide');
    if (!element || !card) return;

    element.scrollBy({
        left: direction * (card.offsetWidth + 16),
        behavior: 'smooth'
    });
};
</script>

<template>
    <div class="review-carousel">
        <div ref="track" class="review-track" aria-label="WordPress customer reviews">
            <div v-for="review in reviews" :key="review.link" class="review-slide">
                <Review :review="review"/>
            </div>
        </div>
        <div class="review-controls" aria-label="Review carousel controls">
            <button type="button" aria-label="Previous review" @click="scrollReviews(-1)">←</button>
            <button type="button" aria-label="Next review" @click="scrollReviews(1)">→</button>
        </div>
    </div>
</template>

<style scoped>
.review-carousel {
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
    position: relative;
}

.review-track {
    display: grid;
    gap: 1rem;
    grid-auto-columns: calc((100% - 2rem) / 3);
    grid-auto-flow: column;
    max-width: 100%;
    overflow-x: hidden;
    padding: 0.15rem 0 0.8rem;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
}

.review-track::-webkit-scrollbar { display: none; }
.review-slide {
    display: flex;
    scroll-snap-align: start;
}

.review-slide :deep(.review-card) { flex: 1 1 auto; }

.review-controls {
    display: flex;
    gap: 0.55rem;
    justify-content: flex-end;
    margin-top: 0.7rem;
}

.review-controls button {
    align-items: center;
    background: #fff;
    border: 1px solid var(--brand-secondary);
    border-radius: 50%;
    color: var(--brand-secondary);
    cursor: pointer;
    display: inline-flex;
    font-size: 1rem;
    height: 2.35rem;
    justify-content: center;
    width: 2.35rem;
}

.review-controls button:hover,
.review-controls button:focus-visible {
    background: var(--brand-secondary-dark);
    color: #fff;
}

@media (max-width: 900px) {
    .review-track { grid-auto-columns: calc((100% - 1rem) / 2); }
}

@media (max-width: 600px) {
    .review-track { grid-auto-columns: 88%; }
}
</style>
